import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiCreditCard,
  FiCalendar,
  FiTrendingUp,
  FiDollarSign,
  FiShoppingBag,
  FiWatch,
  FiArrowRight,
  FiBriefcase,
  FiAward,
  FiMapPin,
} from 'react-icons/fi';
import { walletService, dashboardService } from '../services';
import type { SavedCard } from '../services/wallet.service';
import { getCardBrandColor, formatCardExpiry } from '../utils/cardBrand';
import api from '../services/api';
import type {
  DashboardStats as DashboardStatsType,
} from '../types';
import QuickPaymentsCard from '../components/QuickPaymentsCard';
import PagaliShopCard from '../components/PagaliShopCard';
import RestaurantesCard from '../components/RestaurantesCard';
import RecentTransactionsCard from '../components/RecentTransactionsCard';
import WeekPaymentsChart from '../components/WeekPaymentsChart';
import PublicidadeCard from '../components/PublicidadeCard';
import InteresseModal, { type InteresseTipo } from '../components/InteresseModal';

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendUp?: boolean;
  bgColor: string;
  iconColor: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  trendUp,
  bgColor,
  iconColor,
  onClick,
}) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-2xl p-4 shadow-card hover:shadow-card-hover transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center`}>
        <span className={iconColor}>{icon}</span>
      </div>
      {trend && (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            trendUp ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          }`}
        >
          {trend}
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-xl font-bold text-pagali-gray-800">{value}</p>
      <p className="text-xs text-pagali-gray-500 mt-1">{title}</p>
    </div>
  </div>
);

// Skeleton loader for dashboard
const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    {/* Wallet + Quick Actions */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-[180px] bg-pagali-gray-200 rounded-2xl" />
        <div className="h-[180px] bg-pagali-gray-200 rounded-2xl" />
      </div>
      <div className="grid grid-cols-2 gap-3 content-start">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-[84px] bg-pagali-gray-200 rounded-2xl" />
        ))}
      </div>
    </div>
    {/* Stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-[100px] bg-pagali-gray-200 rounded-2xl" />
      ))}
    </div>
    {/* Content */}
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 h-[280px] bg-pagali-gray-200 rounded-2xl" />
      <div className="lg:col-span-2 h-[280px] bg-pagali-gray-200 rounded-2xl" />
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [walletBalance, setWalletBalance] = useState<any>(null);
  const [stats, setStats] = useState<DashboardStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interesseModal, setInteresseModal] = useState<InteresseTipo | null>(null);
  // null enquanto não se sabe; [] = não tem enrolment feito e mostra-se a CTA.
  const [pagaliCards, setPagaliCards] = useState<SavedCard[] | null>(null);
  const isFoundingMember: boolean = walletBalance?.is_founding_member === true;
  const pagaliCard = pagaliCards?.find((c) => c.isDefault === 'S') ?? pagaliCards?.[0] ?? null;

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Dashboard');

    const loadDashboardData = async () => {
      try {
        const [balance, dashboardStats, savedCards] = await Promise.allSettled([
          walletService.getBalance(),
          dashboardService.getDashboard(),
          walletService.getCards(),
        ]);

        // Falha na leitura dos cartões trata-se como "sem cartão": mostra a CTA,
        // que é inofensiva, em vez de deixar a coluna em branco.
        setPagaliCards(savedCards.status === 'fulfilled' ? savedCards.value : []);

        if (balance.status === 'fulfilled') {
          setWalletBalance(balance.value);
        } else {
          // Fallback: try direct wallet endpoint with userId from auth
          try {
            const { getAuth } = await import('firebase/auth');
            const user = getAuth().currentUser;
            if (user) {
              const token = await user.getIdTokenResult();
              const mysqlId = (token.claims as any).mysqlId;
              if (mysqlId) {
                const resp = await api.get(`/wallet/${mysqlId}`);
                if (resp.data.success && resp.data.data) {
                  const w = resp.data.data;
                  setWalletBalance({
                    saldo: typeof w.saldo === 'number' ? w.saldo : parseFloat(w.saldo) || 0,
                    saldo_disponivel: typeof w.saldo_disponivel === 'number' ? w.saldo_disponivel : parseFloat(w.saldo_disponivel || w.saldo) || 0,
                    saldo_bonus: 0, saldo_bloqueado: 0, moeda: 'CVE', status: 'ACTIVE',
                  });
                }
              }
            }
          } catch { /* ignore */ }
        }

        if (dashboardStats.status === 'fulfilled') setStats(dashboardStats.value);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('dashboard.errorLoading')
        );
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatAmount = (amount: number | undefined | null): string => {
    const value = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
      minimumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-pagali-error mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section: Wallet + Pagali Card, e as acções rápidas à direita */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Duas colunas: saldo à esquerda, cartão tokenizado à direita */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="wallet-card cursor-pointer hover:scale-[1.01] transition-transform"
            style={{
              background: isFoundingMember
                ? 'linear-gradient(135deg, #7340BE 0%, #4A1080 100%)'
                : 'linear-gradient(135deg, #00B383 0%, #00795A 100%)',
            }}
            onClick={() => navigate('/wallet')}
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white transform translate-x-1/3 -translate-y-1/3" />
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white transform -translate-x-1/3 translate-y-1/3" />
            </div>
            <div className="wallet-card-content">
              <div className="flex justify-between items-start">
                <div>
                  {isFoundingMember ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-amber-300/60 bg-amber-400/20 text-amber-200">
                      🏅 Membro Fundador
                    </span>
                  ) : (
                    <p className="text-xs text-white/70 uppercase tracking-wider">Pagali Wallet</p>
                  )}
                  <p className="text-lg font-semibold mt-1">{t('dashboard.myWallet')}</p>
                </div>
                <div className="w-10 h-8 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded opacity-80" />
              </div>
              <div className="mt-auto flex items-end justify-between">
                <div>
                  <p className="text-xs text-white/70 uppercase">{t('dashboard.availableBalance')}</p>
                  <p className="font-semibold text-3xl">{formatAmount(walletBalance?.saldo_disponivel)}</p>
                  {walletBalance?.saldo_cashback > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate('/cashback'); }}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/20 hover:bg-yellow-400/30 transition-colors"
                    >
                      <span className="text-yellow-300 text-xs">💰</span>
                      <span className="text-yellow-200 text-xs font-medium">
                        Cashback: {formatAmount(walletBalance.saldo_cashback)}
                      </span>
                      <FiArrowRight className="text-yellow-300 text-xs" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1 text-white/60 text-sm">
                  <span>{t('common.seeDetails')}</span>
                  <FiArrowRight />
                </div>
              </div>
            </div>
          </div>

          {/* Pagali Card — o cartão tokenizado. Sem enrolment feito, mostra a
              CTA em vez de um cartão vazio. O enrolment em si vive no /wallet
              (popup + polling do SISP); aqui só se encaminha para lá. */}
          {pagaliCard ? (
            <div
              onClick={() => navigate('/wallet')}
              className={`wallet-card p-5 text-white bg-gradient-to-br ${getCardBrandColor(pagaliCard.cardType)}`}
            >
              {/* Mesmos círculos decorativos do cartão na carteira */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white transform translate-x-1/4 -translate-y-1/4" />
                <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white transform -translate-x-1/4 translate-y-1/4" />
              </div>

              <div className="relative flex flex-col h-full">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs text-white/70 uppercase tracking-wider font-medium">
                    {pagaliCard.cardType || 'Cartão'}
                  </span>
                  <span className="text-[10px] text-white/60 uppercase tracking-wider">
                    Pagali Card
                  </span>
                </div>

                <p className="text-lg tracking-[0.25em] font-mono mb-4">
                  •••• •••• •••• {pagaliCard.lastFourDigits}
                </p>

                <div className="mt-auto flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] text-white/60 uppercase">{t('wallet.cardHolder')}</p>
                    <p className="text-sm font-medium truncate max-w-[180px]">
                      {pagaliCard.cardHolder || t('wallet.cardHolder')}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-white/60 uppercase">{t('wallet.validThru')}</p>
                    <p className="text-sm font-medium tabular-nums">{formatCardExpiry(pagaliCard)}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/wallet', { state: { abrirPagaliCard: true } })}
              disabled={pagaliCards === null}
              className="wallet-card text-left w-full border-2 border-dashed border-pagali-purple/30 bg-pagali-purple/[0.04] hover:border-pagali-purple/60 hover:bg-pagali-purple/[0.07] disabled:opacity-60 disabled:cursor-wait transition-colors"
            >
              <div className="absolute inset-0 p-5 flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-pagali-purple/70 uppercase tracking-wider">
                      Pagali Card
                    </p>
                    <p className="text-lg font-semibold mt-1 text-pagali-gray-800">
                      {pagaliCards === null
                        ? t('dashboard.loadingCard')
                        : t('dashboard.noCardTitle')}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-pagali-purple/10 flex items-center justify-center shrink-0">
                    <FiCreditCard className="text-xl text-pagali-purple" />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-pagali-gray-600 leading-relaxed">
                    {t('dashboard.noCardSubtitle')}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-pagali-purple">
                    {t('dashboard.createCard')}
                    <FiArrowRight />
                  </span>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Acções rápidas em 2×2, não em coluna. A altura desta linha é ditada
            pelo item mais alto: com as quatro empilhadas, a Wallet e o Pagali
            Card esticavam-se para ~380px e deixavam de parecer cartões. */}
        <div className="grid grid-cols-2 gap-3 content-start">
          <button
            onClick={() => setInteresseModal('promotor')}
            className="flex items-center gap-2.5 p-3 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-200 hover:border-violet-500 hover:shadow-md transition-all group"
          >
            <div className="w-9 h-9 shrink-0 rounded-xl bg-violet-500/10 flex items-center justify-center group-hover:bg-violet-500/20 transition-colors">
              <FiCalendar className="text-lg text-violet-600" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-[13px] font-semibold leading-tight text-violet-700">{t('dashboard.eventPromoter')}</p>
              <p className="text-[10px] leading-snug mt-0.5 text-violet-600/70">{t('dashboard.eventPromoterSubtitle')}</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/pulseira')}
            className="flex items-center gap-2.5 p-3 bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl border border-pink-200 hover:border-pink-400 hover:shadow-md transition-all group"
          >
            <div className="w-9 h-9 shrink-0 rounded-xl bg-pink-500/10 flex items-center justify-center group-hover:bg-pink-500/20 transition-colors">
              <FiWatch className="text-lg text-pink-600" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-[13px] font-semibold leading-tight text-pink-700">{t('dashboard.nfcBracelet')}</p>
              <p className="text-[10px] leading-snug mt-0.5 text-pink-600/70">{t('dashboard.cashlessEvents')}</p>
            </div>
          </button>

          <button
            onClick={() => setInteresseModal('parceiro')}
            className="flex items-center gap-2.5 p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 hover:border-amber-400 hover:shadow-md transition-all group"
          >
            <div className="w-9 h-9 shrink-0 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
              <FiBriefcase className="text-lg text-amber-600" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-[13px] font-semibold leading-tight text-amber-700">{t('dashboard.becomePartner')}</p>
              <p className="text-[10px] leading-snug mt-0.5 text-amber-600/70">{t('dashboard.partnerSubtitle')}</p>
            </div>
          </button>

          <button
            onClick={() => setInteresseModal('agente')}
            className="flex items-center gap-2.5 p-3 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-2xl border border-sky-200 hover:border-sky-400 hover:shadow-md transition-all group"
          >
            <div className="w-9 h-9 shrink-0 rounded-xl bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500/20 transition-colors">
              <FiMapPin className="text-lg text-sky-600" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-[13px] font-semibold leading-tight text-sky-700">{t('dashboard.becomeAgent')}</p>
              <p className="text-[10px] leading-snug mt-0.5 text-sky-600/70">{t('dashboard.agentSubtitle')}</p>
            </div>
          </button>
        </div>
      </div>

      {/* Key Stats — 5 cartões: 2 contagens (transacções, compras) e 3 valores
          em CVE (mês, últimos 7 dias, total pago desde sempre). */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          title={t('dashboard.transactions')}
          value={stats?.totalTransacoes || 0}
          icon={<FiTrendingUp className="text-lg" />}
          trend={stats?.percentagemTransacoes ? `${stats.percentagemTransacoes}%` : undefined}
          trendUp={(stats?.percentagemTransacoes || 0) >= 0}
          bgColor="bg-pagali-purple/10"
          iconColor="text-pagali-purple"
          onClick={() => navigate('/transactions')}
        />
        <StatCard
          title={t('dashboard.paymentsThisMonth')}
          value={formatAmount(stats?.totalMes || 0)}
          icon={<FiDollarSign className="text-lg" />}
          bgColor="bg-green-100"
          iconColor="text-green-600"
          onClick={() => navigate('/payments')}
        />
        <StatCard
          title={t('dashboard.purchases')}
          value={stats?.totalCompras || 0}
          icon={<FiShoppingBag className="text-lg" />}
          bgColor="bg-blue-100"
          iconColor="text-blue-600"
          onClick={() => navigate('/ecommerce')}
        />
        <StatCard
          title={t('dashboard.last7Days')}
          value={formatAmount(stats?.totalUltimos7Dias || 0)}
          icon={<FiCreditCard className="text-lg" />}
          bgColor="bg-orange-100"
          iconColor="text-orange-600"
          onClick={() => navigate('/transactions')}
        />
        <StatCard
          title={t('dashboard.totalPaid')}
          value={formatAmount(stats?.totalPago || 0)}
          icon={<FiAward className="text-lg" />}
          bgColor="bg-pagali-lime/20"
          iconColor="text-pagali-purple"
          onClick={() => navigate('/transactions')}
        />
      </div>

      {/* Quick Payments + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <QuickPaymentsCard />
        </div>
        <div className="lg:col-span-2">
          <WeekPaymentsChart />
        </div>
      </div>

      {/* Advertising Banner */}
      <PublicidadeCard />

      {/* Pagali Shop & Services */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PagaliShopCard />
        <RestaurantesCard />
      </div>

      {/* Recent Transactions */}
      <RecentTransactionsCard showFilters={true} limit={8} />

      {/* Interesse Modal */}
      {interesseModal && (
        <InteresseModal
          tipo={interesseModal}
          onClose={() => setInteresseModal(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
